import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { withAppHOC } from "../../hoc";
import API from "../../api";
import { API_RESPONSE_TYPES } from "../../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBank,
  faClose,
  faExclamation,
  faIndianRupeeSign,
  faListDots,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import { formatCurrency } from "../../utils";
import { Grid, GridItem } from "../../components/Grid";
import { TableBuilder } from "../../components/TableElements";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

/* =======================
   Styled Components
======================= */

const StyledHeading = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 16px 0 12px;
`;

const StyledCountersContainer = styled.section`
  width: 100%;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 28px;
  background-color: #f8fafc;
`;

const StyledCounter = styled.section`
  width: 100%;
  min-width: 300px;
  display: flex;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e2e8ee;
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  /* Gradient top bar */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 5px;
    width: 100%;
    background: ${({ borderTopColor }) =>
    borderTopColor ||
    "linear-gradient(90deg, #667eea, #764ba2)"};
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.12);
  }
`;

const StyledCounterIcon = styled.section`
  padding: 22px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 28px;
    height: 28px;
    padding: 14px;
    color: #ffffff;
    border-radius: 10px;
    background: ${({ iconBgColor }) =>
    iconBgColor ||
    "linear-gradient(135deg, #667eea, #764ba2)"};
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
  }
`;

const StyledCounterData = styled.section`
  flex: 1;
  padding: 20px 0;

  p {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    margin: 0 0 6px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  h2 {
    font-family: "Space Grotesk", sans-serif;
    font-weight: 600;
    font-size: 28px;
    color: #0f172a;
    margin: 6px 0 14px;
  }

  span {
    font-size: 11px;
    font-weight: 700;
    padding: 6px 14px;
    border-radius: 999px;
    color: ${({ spanTextColour }) => spanTextColour};
    background: ${({ spanBgColour }) =>
    spanBgColour ||
    "linear-gradient(90deg, #e0e7ff, #f0f4ff)"};
    display: inline-block;
  }
`;

/* =======================
   Component
======================= */

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState();

  const readStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await API.get("/dashboard/stats-new");
      const { status, data } = response.data;
      if (status === API_RESPONSE_TYPES.SUCCESS) {
        setStats(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    readStats();
  }, [readStats]);

  return (
    <StyledCountersContainer>
      {isLoading ? (
        <LoadingSpinner isLoading={isLoading} />
      ) : (
        <>
          <Grid>
            <GridItem>
              <StyledCounter borderTopColor="linear-gradient(90deg,#00c853,#64dd17)">
                <StyledCounterIcon iconBgColor="linear-gradient(135deg,#00c853,#64dd17)">
                  <FontAwesomeIcon icon={faIndianRupeeSign} />
                </StyledCounterIcon>
                <StyledCounterData
                  spanBgColour="linear-gradient(90deg,#e8f5e9,#c8e6c9)"
                  spanTextColour="#1b5e20"
                >
                  <p>Pending Transaction</p>
                  <h2>{stats?.pendingTransaction}</h2>
                  <span>Today</span>
                </StyledCounterData>
              </StyledCounter>
            </GridItem>

            <GridItem>
              <StyledCounter borderTopColor="linear-gradient(90deg,#ff416c,#ff4b2b)">
                <StyledCounterIcon iconBgColor="linear-gradient(135deg,#ff416c,#ff4b2b)">
                  <FontAwesomeIcon icon={faExclamation} />
                </StyledCounterIcon>
                <StyledCounterData
                  spanBgColour="linear-gradient(90deg,#ffe9eb,#ffd6db)"
                  spanTextColour="#b71c1c"
                >
                  <p>Pending Complaints</p>
                  <h2>{stats?.pendingComplaints}</h2>
                  <span>Today</span>
                </StyledCounterData>
              </StyledCounter>
            </GridItem>

            <GridItem>
              <StyledCounter borderTopColor="linear-gradient(90deg,#36d1dc,#5b86e5)">
                <StyledCounterIcon iconBgColor="linear-gradient(135deg,#36d1dc,#5b86e5)">
                  <FontAwesomeIcon icon={faClose} />
                </StyledCounterIcon>
                <StyledCounterData
                  spanBgColour="linear-gradient(90deg,#e3f2fd,#bbdefb)"
                  spanTextColour="#0d47a1"
                >
                  <p>Fake Complaints Resolved</p>
                  <h2>{stats?.fakeComplaintsResolved}</h2>
                  <span>Today</span>
                </StyledCounterData>
              </StyledCounter>
            </GridItem>
            <GridItem>
              <StyledCounter borderTopColor="linear-gradient(90deg,#12c2e9,#c471ed,#f64f59)">
                <StyledCounterIcon iconBgColor="linear-gradient(135deg,#12c2e9,#c471ed)">
                  <FontAwesomeIcon icon={faListDots} />
                </StyledCounterIcon>
                <StyledCounterData
                  spanBgColour="linear-gradient(90deg,#e0f7fa,#f3e5f5)"
                  spanTextColour="#4a148c"
                >
                  <p>Total Transaction</p>
                  <h2>{formatCurrency(stats?.todaysTransactionsTotal)}</h2>
                  <span>Today</span>
                </StyledCounterData>
              </StyledCounter>
            </GridItem>
          </Grid>

          <Grid>
            <GridItem>
              <StyledCounter borderTopColor="linear-gradient(90deg,#11998e,#38ef7d)">
                <StyledCounterIcon iconBgColor="linear-gradient(135deg,#11998e,#38ef7d)">
                  <FontAwesomeIcon icon={faWallet} />
                </StyledCounterIcon>
                <StyledCounterData
                  spanBgColour="linear-gradient(90deg,#e0f2f1,#ccff90)"
                  spanTextColour="#1b5e20"
                >
                  <p>Wallet Topup</p>
                  <h2>{formatCurrency(stats?.todayWalletTopup)}</h2>
                  <span>Today</span>
                </StyledCounterData>
              </StyledCounter>
            </GridItem>

            <GridItem>
              <StyledCounter borderTopColor="linear-gradient(90deg,#8e2de2,#4a00e0)">
                <StyledCounterIcon iconBgColor="linear-gradient(135deg,#8e2de2,#4a00e0)">
                  <FontAwesomeIcon icon={faWallet} />
                </StyledCounterIcon>
                <StyledCounterData
                  spanBgColour="linear-gradient(90deg,#ede7f6,#d1c4e9)"
                  spanTextColour="#311b92"
                >
                  <p>Buyers Wallet</p>
                  <h2>{formatCurrency(stats?.userBuyerWallet)}</h2>
                  <span>Today</span>
                </StyledCounterData>
              </StyledCounter>
            </GridItem>

            <GridItem>
              <StyledCounter borderTopColor="linear-gradient(90deg,#ff0084,#ff8c00)">
                <StyledCounterIcon iconBgColor="linear-gradient(135deg,#ff0084,#ff8c00)">
                  <FontAwesomeIcon icon={faWallet} />
                </StyledCounterIcon>
                <StyledCounterData
                  spanBgColour="linear-gradient(90deg,#fce4ec,#ffe0b2)"
                  spanTextColour="#880e4f"
                >
                  <p>Sellers Wallet</p>
                  <h2>{formatCurrency(stats?.userSellerWallet)}</h2>
                  <span>Today</span>
                </StyledCounterData>
              </StyledCounter>
            </GridItem>

            <GridItem>
              <StyledCounter borderTopColor="linear-gradient(90deg,#f7971e,#ffd200)">
                <StyledCounterIcon iconBgColor="linear-gradient(135deg,#f7971e,#ffd200)">
                  <FontAwesomeIcon icon={faBank} />
                </StyledCounterIcon>
                <StyledCounterData
                  spanBgColour="linear-gradient(90deg,#fff8e1,#ffecb3)"
                  spanTextColour="#e65100"
                >
                  <p>Wallet Redeem</p>
                  <h2>{formatCurrency(stats?.todayWalletRedeem)}</h2>
                  <span>Today</span>
                </StyledCounterData>
              </StyledCounter>
            </GridItem>
          </Grid>

          <Grid>
            <GridItem>
              <StyledHeading>Sales Summary</StyledHeading>
              <TableBuilder
                tableHeadings={[
                  { title: "Date", dataSelector: "date", dataType: "string" },
                  {
                    title: "Amount",
                    dataSelector: "amount",
                    dataType: "string",
                    CellRenderer: (value) => formatCurrency(value),
                  },
                  {
                    title: "Profit",
                    dataSelector: "profit",
                    dataType: "string",
                    CellRenderer: (value) => formatCurrency(value),
                  },
                ]}
                tableData={stats?.transactionsSummary}
              />
            </GridItem>

            <GridItem>
              <StyledHeading>Recent User Registrations</StyledHeading>
              <TableBuilder
                tableHeadings={[
                  {
                    title: "User Details",
                    dataSelector: "fullName",
                    dataType: "string",
                  },
                  {
                    title: "Company Name",
                    dataSelector: "firmName",
                    dataType: "string",
                    CellRenderer: (value) => (value ? value : "N/A"),
                  },
                  {
                    title: "Mobile",
                    dataSelector: "mobileNumber",
                    dataType: "string",
                  },
                  {
                    title: "Date",
                    dataSelector: "createdOn",
                    dataType: "string",
                  },
                ]}
                tableData={stats?.recentAddedUsers}
              />
            </GridItem>
          </Grid>

          <Grid>
            <GridItem>
              <StyledHeading>Todays Seller's Sales</StyledHeading>
              <TableBuilder
                tableHeadings={[
                  {
                    title: "Company Name",
                    dataSelector: "companyName",
                    dataType: "string",
                  },
                  {
                    title: "Amount",
                    dataSelector: "amount",
                    dataType: "string",
                    CellRenderer: (value) => formatCurrency(value),
                  },
                ]}
                tableData={stats?.sellerSales}
              />
            </GridItem>

            <GridItem>
              <StyledHeading>Todays Buyer's Sales</StyledHeading>
              <TableBuilder
                tableHeadings={[
                  {
                    title: "Company Name",
                    dataSelector: "companyName",
                    dataType: "string",
                  },
                  {
                    title: "Amount",
                    dataSelector: "amount",
                    dataType: "string",
                    CellRenderer: (value) => formatCurrency(value),
                  },
                ]}
                tableData={stats?.buyerSales}
              />
            </GridItem>
          </Grid>
        </>
      )}
    </StyledCountersContainer>
  );
};

export default withAppHOC(Dashboard);
